import React, { useEffect, useMemo, useState } from 'react';
import {
  getCurrentUserProfile,
  getUsernameChangeCooldownMs,
  updateCurrentUserProfile,
} from './cognitoAuth';
import './UserSettingsPage.css';

const USERNAME_CHANGE_COOLDOWN_DAYS = Math.round(getUsernameChangeCooldownMs() / (24 * 60 * 60 * 1000));

function formatNextAvailableDate(timestamp) {
  if (!timestamp) {
    return 'now';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function UserSettingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    preferredUsername: '',
    gender: '',
    phoneNumber: '',
    email: '',
    picture: '',
  });

  const [imagePreview, setImagePreview] = useState('');
  const [section, setSection] = useState('profile');

  useEffect(() => {
    let isMounted = true;

    async function loadProfile() {
      setIsLoading(true);
      setError('');

      try {
        const loadedProfile = await getCurrentUserProfile();

        if (!loadedProfile) {
          window.location.assign('/login');
          return;
        }

        if (!isMounted) {
          return;
        }

        setProfile(loadedProfile);
        setForm({
          preferredUsername: loadedProfile.preferredUsername || '',
          gender: loadedProfile.gender || '',
          phoneNumber: loadedProfile.phoneNumber || '',
          email: loadedProfile.email || '',
          picture: loadedProfile.picture || '',
        });
        setImagePreview(loadedProfile.picture || '');
      } catch (loadError) {
        if (isMounted) {
          setError(loadError?.message || 'Unable to load your settings.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  const nextUsernameChangeLabel = useMemo(() => {
    if (!profile?.nextProfileChangeAt) {
      return 'You can change username, email, or phone now.';
    }

    if (profile.canChangeProfile) {
      return 'You can change username, email, or phone now.';
    }

    return `You can change username, email, or phone again on ${formatNextAvailableDate(profile.nextProfileChangeAt)}.`;
  }, [profile]);


  function handleFieldChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleImageChange(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImagePreview(dataUrl);
      setForm((current) => ({ ...current, picture: dataUrl }));
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!profile) {
      setError('Unable to update settings right now.');
      return;
    }

    if (!form.preferredUsername.trim()) {
      setError('Username cannot be empty.');
      return;
    }

    if (!form.gender.trim()) {
      setError('Please select a gender.');
      return;
    }

    if (form.phoneNumber.trim() && !/^\+[1-9]\d{7,14}$/.test(form.phoneNumber.trim())) {
      setError('Phone number must use international format, like +14805551234.');
      return;
    }

    setIsSaving(true);

    try {
      const result = await updateCurrentUserProfile({
        preferredUsername: form.preferredUsername,
        gender: form.gender,
        phoneNumber: form.phoneNumber,
        email: form.email,
        picture: form.picture,
        currentProfile: profile,
      });

      if (!result.updated) {
        setMessage('No changes were needed.');
      } else {
        setMessage('Your settings have been updated.');
      }

      const refreshedProfile = await getCurrentUserProfile();
      setProfile(refreshedProfile);
      setForm({
        preferredUsername: refreshedProfile?.preferredUsername || '',
        gender: refreshedProfile?.gender || '',
        phoneNumber: refreshedProfile?.phoneNumber || '',
        email: refreshedProfile?.email || '',
        picture: refreshedProfile?.picture || '',
      });
      setImagePreview(refreshedProfile?.picture || '');
      
      window.dispatchEvent(new Event('auth-profile-updated'));
    } catch (saveError) {
      setError(saveError?.message || 'Unable to save your settings.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="settings-page-wrapper">
      <div className="settings-shell">
        <aside className="settings-nav">
          <ul>
            <li className={`settings-nav-item ${section === 'profile' ? 'active' : ''}`} onClick={() => setSection('profile')}>Profile</li>
            <li className={`settings-nav-item ${section === 'security' ? 'active' : ''}`} onClick={() => setSection('security')}>Security</li>
            <li className={`settings-nav-item ${section === 'notifications' ? 'active' : ''}`} onClick={() => setSection('notifications')}>Notifications</li>
            <li className={`settings-nav-item ${section === 'billing' ? 'active' : ''}`} onClick={() => setSection('billing')}>Billing</li>
          </ul>
        </aside>

        <section className="settings-panel settings-panel-form">
          <div className="settings-header-row">
            <div>
              <h2 className="settings-title">Profile settings</h2>
              <p className="settings-subtitle">Make updates to your displayed profile information.</p>
            </div>
            <button type="button" className="settings-back-link" onClick={() => window.location.assign('/')}>
              Back home
            </button>
          </div>

          {isLoading ? (
            <div className="settings-feedback settings-muted">Loading your settings...</div>
          ) : section === 'profile' ? (
            <form className="settings-form" onSubmit={handleSubmit}>
              <div>
                <label className="settings-label" htmlFor="preferredUsername">Displayed username</label>
                <input
                  id="preferredUsername"
                  name="preferredUsername"
                  type="text"
                  className="settings-input"
                  value={form.preferredUsername}
                  onChange={handleFieldChange}
                  disabled={isSaving || !profile?.canChangeProfile}
                  maxLength={50}
                />
                <p className="settings-field-hint">
                  This updates your Cognito `preferred_username` and can only be changed once every {USERNAME_CHANGE_COOLDOWN_DAYS} days.
                </p>
                <p className={`settings-cooldown ${profile?.canChangeProfile ? 'available' : 'locked'}`}>
                  {nextUsernameChangeLabel}
                </p>
              </div>

              <div>
                <label className="settings-label" htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  className="settings-input"
                  value={form.gender}
                  onChange={handleFieldChange}
                  disabled={isSaving}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="settings-label" htmlFor="phoneNumber">Phone number</label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  className="settings-input"
                  value={form.phoneNumber}
                  onChange={handleFieldChange}
                  disabled={isSaving}
                  placeholder="+14805551234"
                />
                <p className="settings-field-hint">Use international format with a leading `+`. Cognito may require verification before the number is fully trusted.</p>
              </div>

              <div>
                <label className="settings-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="settings-input"
                  value={form.email}
                  onChange={handleFieldChange}
                  disabled={isSaving}
                  placeholder="you@example.com"
                />
                <p className="settings-field-hint">Changing email may require verification. Email changes are limited to once every 30 days.</p>
              </div>

              <div>
                <label className="settings-label">Profile image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: 72, height: 72, borderRadius: 12, overflow: 'hidden', background: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {imagePreview ? <img src={imagePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="fas fa-user" style={{ fontSize: '28px', color: '#777' }} />}
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageChange} disabled={isSaving} />
                </div>
                <p className="settings-field-hint">Upload a square profile image. This demo stores the image as a data URL as your profile picture attribute.</p>
              </div>

              {/* tags field removed */}

              

              <div className="settings-actions">
                <button type="submit" className="settings-submit" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save settings'}
                </button>
              </div>

              <div className="settings-feedback" aria-live="polite">
                {error ? <span className="settings-error">{error}</span> : null}
                {!error && message ? <span className="settings-success">{message}</span> : null}
              </div>
            </form>
            ) : (
              <div style={{ padding: 24 }}>
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>{section.charAt(0).toUpperCase() + section.slice(1)} settings</h3>
                <p style={{ color: '#666' }}>This section is not implemented in the demo yet.</p>
              </div>
            )}
        </section>
      </div>
    </div>
  );
}

export default UserSettingsPage;